import api from './api';

export interface OfferStandalonePackageDTO {
  package_id: string;
  offer_id: string;
  sequence_number?: number;
  package_symbol?: string;
  name: string;
  package_type?: 'materiały' | 'usługi';
  description?: string;
  parameters?: string;
  estimated_value?: number;
  documentation_link?: string;
  package_documentation_link?: string;
  created_at?: string;
  updated_at?: string;
  created_by_user_id?: string;
  created_by_name?: string;
  created_by_avatar_url?: string;
  inquiries_count?: number;
  offers_count?: number;
}

export interface OfferStandaloneInquiryDTO {
  inquiry_id: string;
  offer_id: string;
  package_id?: string;
  contractor_email: string;
  contractor_phone_1?: string;
  contractor_phone_2?: string;
  subject: string;
  status: 'Przygotowane' | 'draft gmail' | 'Wysłane' | 'Odpowiedź' | 'Odmówiono';
  notes?: string;
  last_contact_date?: string;
  planned_contact_date?: string;
  gmail_draft_id?: string;
  gmail_message_id?: string;
  gmail_thread_id?: string;
  gmail_reply_message_id?: string;
  created_by_user_id?: string;
  created_at?: string;
  updated_at?: string;
  invite_to_collectifile_board?: boolean;
}

export interface OfferStandaloneReceivedOfferDTO {
  offer_id: string;
  offer_standalone_id: string;
  inquiry_id?: string;
  package_id?: string;
  package_name?: string;
  contractor_name: string;
  offer_type?: 'materiały' | 'usługi';
  offered_price?: number;
  status?: 'Aktywna' | 'Zaakceptowana' | 'Odrzucona';
  documentation_link?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
  created_by_name?: string;
  created_by_avatar_url?: string;
  received_offer_id?: string;
}

class OfferStandaloneManageService {
  private base = '/offers-standalone/manage';

  async getPackages(offerId: string): Promise<OfferStandalonePackageDTO[]> {
    const res = await api.get(`${this.base}/${offerId}/packages`);
    return res.data;
  }

  async createPackage(offerId: string, data: Partial<OfferStandalonePackageDTO>, files?: File[]): Promise<OfferStandalonePackageDTO> {
    // Zachowaj __source jeśli jest ustawione (dla pakietów generowanych przez AI)
    // Jeśli nie jest ustawione, backend automatycznie przypisze pakiet do zalogowanego użytkownika
    if (files && files.length) {
      const form = new FormData();
      form.append('data', JSON.stringify(data));
      files.forEach((f) => form.append('files', f));
      const res = await api.post(`${this.base}/${offerId}/packages`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      return res.data;
    }
    const res = await api.post(`${this.base}/${offerId}/packages`, data);
    return res.data;
  }

  async updatePackage(offerId: string, packageId: string, data: Partial<OfferStandalonePackageDTO>): Promise<OfferStandalonePackageDTO> {
    const res = await api.patch(`${this.base}/${offerId}/packages/${packageId}`, data);
    return res.data;
  }

  async deletePackage(offerId: string, packageId: string): Promise<{ success: boolean }> {
    const res = await api.delete(`${this.base}/${offerId}/packages/${packageId}`);
    return res.data;
  }

  async getInquiries(offerId: string): Promise<OfferStandaloneInquiryDTO[]> {
    const res = await api.get(`${this.base}/${offerId}/inquiries`);
    return res.data;
  }

  async getInquiryEmailBody(offerId: string, inquiryId: string): Promise<{ to: string; subject: string; body: string }> {
    const res = await api.get(`${this.base}/${offerId}/inquiries/${inquiryId}/email-body`);
    return res.data.data;
  }

  async createInquiry(offerId: string, data: Partial<OfferStandaloneInquiryDTO>): Promise<OfferStandaloneInquiryDTO> {
    const res = await api.post(`${this.base}/${offerId}/inquiries`, data);
    return res.data;
  }

  async updateInquiry(offerId: string, inquiryId: string, data: Partial<OfferStandaloneInquiryDTO>): Promise<OfferStandaloneInquiryDTO> {
    const res = await api.patch(`${this.base}/${offerId}/inquiries/${inquiryId}`, data);
    return res.data;
  }

  async deleteInquiry(offerId: string, inquiryId: string): Promise<{ success: boolean }> {
    const res = await api.delete(`${this.base}/${offerId}/inquiries/${inquiryId}`);
    return res.data;
  }

  async updateInquiryStatus(offerId: string, inquiryId: string, status: OfferStandaloneInquiryDTO['status']): Promise<OfferStandaloneInquiryDTO> {
    const res = await api.patch(`${this.base}/${offerId}/inquiries/${inquiryId}/status`, { status });
    return res.data;
  }

  async createInquiryGmailDraft(offerId: string, inquiryId: string): Promise<any> {
    const res = await api.post(`${this.base}/${offerId}/inquiries/${inquiryId}/create-gmail-draft`);
    return res.data;
  }

  async sendInquiryGmailDraft(offerId: string, inquiryId: string, draftId?: string): Promise<any> {
    const res = await api.post(`${this.base}/${offerId}/inquiries/${inquiryId}/send-gmail-draft`, { draftId });
    return res.data;
  }

  async sendInquiryDirectEmail(offerId: string, inquiryId: string): Promise<any> {
    const res = await api.post(`${this.base}/${offerId}/inquiries/${inquiryId}/send-direct-email`);
    return res.data;
  }

  async checkRepliesForInquiry(offerId: string, inquiryId: string): Promise<any> {
    const res = await api.post(`${this.base}/${offerId}/inquiries/${inquiryId}/check-replies`);
    return res.data;
  }

  async getReceivedOffers(offerId: string): Promise<OfferStandaloneReceivedOfferDTO[]> {
    const res = await api.get(`${this.base}/${offerId}/received-offers`);
    return res.data;
  }

  async createReceivedOffer(offerId: string, data: Partial<OfferStandaloneReceivedOfferDTO>): Promise<OfferStandaloneReceivedOfferDTO> {
    const res = await api.post(`${this.base}/${offerId}/received-offers`, data);
    return res.data;
  }

  async updateReceivedOffer(offerId: string, receivedOfferId: string, data: Partial<OfferStandaloneReceivedOfferDTO>): Promise<OfferStandaloneReceivedOfferDTO> {
    const res = await api.patch(`${this.base}/${offerId}/received-offers/${receivedOfferId}`, data);
    return res.data;
  }

  async deleteReceivedOffer(offerId: string, receivedOfferId: string): Promise<{ success: boolean }> {
    const res = await api.delete(`${this.base}/${offerId}/received-offers/${receivedOfferId}`);
    return res.data;
  }
}

export const offerStandaloneManageService = new OfferStandaloneManageService();


